import * as React from 'react';

import { cn } from '@rematter/ui-web';

import { useTranslations } from '../../i18n/useTranslations';

import {
  emailActionDefinition,
  httpActionDefinition,
  conditionDefinition,
  delayDefinition,
  approvalDefinition,
  filterDefinition,
  switchDefinition,
  loopDefinition,
  getColorClasses,
  type BaseNodeDefinition,
  NodeDefinition,
} from './nodes';

type NodePaletteProps = {
  className?: string;
  onAddNode?: (definition: BaseNodeDefinition) => void;
};

type PaletteSection = {
  nameKey: 'notifications' | 'http' | 'userAction' | 'flowControl';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nodes: NodeDefinition<any>[];
};

const PALETTE_SECTIONS: PaletteSection[] = [
  {
    nameKey: 'notifications',
    nodes: [emailActionDefinition],
  },
  {
    nameKey: 'http',
    nodes: [httpActionDefinition],
  },
  {
    nameKey: 'userAction',
    nodes: [approvalDefinition],
  },
  {
    nameKey: 'flowControl',
    nodes: [
      conditionDefinition,
      delayDefinition,
      filterDefinition,
      switchDefinition,
      loopDefinition,
    ],
  },
];

export const NodePalette: React.FC<NodePaletteProps> = ({
  className,
  onAddNode,
}) => {
  const t = useTranslations();

  const onDragStart = (
    event: React.DragEvent,
    definition: BaseNodeDefinition,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    event.dataTransfer.setData('application/reactflow', definition.key);
    event.dataTransfer.setData('integrationKey', definition.key);
    event.dataTransfer.setData('integrationName', definition.label);
    event.dataTransfer.setData('integrationData', JSON.stringify(definition));
    event.dataTransfer.setData('dragOffsetX', String(offsetX));
    event.dataTransfer.setData('dragOffsetY', String(offsetY));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      className={cn(
        '@NodePalette',
        'flex h-full w-64 flex-col border-r border-neutral/10 bg-neutral/5',
        className,
      )}
    >
      <div className="flex-1 overflow-y-auto p-4">
        {PALETTE_SECTIONS.map(({ nameKey, nodes }) => (
          <div key={nameKey} className="mb-6 last:mb-0">
            <h4 className="mb-2 text-xs font-semibold tracking-wider text-neutral/50 uppercase">
              {t.editor.palette.categories[nameKey]}
            </h4>
            <div className="space-y-2">
              {nodes.map((definition) => {
                const Icon = definition.icon;
                const colors = getColorClasses(definition.color);

                return (
                  <div
                    key={definition.key}
                    role="button"
                    tabIndex={0}
                    draggable
                    onDragStart={(e) => onDragStart(e, definition)}
                    onClick={() => onAddNode?.(definition)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onAddNode?.(definition);
                      }
                    }}
                    className={cn(
                      'cursor-grab rounded border border-neutral/20 bg-white p-3 transition-shadow hover:shadow-md',
                      'active:cursor-grabbing active:opacity-50',
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className={cn('rounded p-1.5', colors.bg, colors.text)}
                      >
                        <Icon className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {definition.label}
                        </div>
                        {definition.description && (
                          <div className="mt-0.5 line-clamp-2 text-xs text-neutral/50">
                            {definition.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {PALETTE_SECTIONS.length === 0 && (
          <div className="py-8 text-center text-sm text-neutral/50">
            {t.editor.palette.noNodes}
          </div>
        )}
      </div>
    </div>
  );
};
